/**
 * TestModeActivityViewer — Test Mode session log.
 *
 * The Test Mode session table is surfaced via /v3/sessions (filtered to
 * `isTestMode = true`). When the dedicated `/v3/audit/test-mode-activity`
 * route lands, swap the fetch.
 */

import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { downloadCsv, formatLocal, rowsToCsv } from '@/utils/time';

interface TestModeRow {
  sessionId: string;
  startedAt?: string;
  endedAt?: string;
  actor?: string;
  branch?: string;
  cartCount?: number;
}

type DateRange = '24h' | '7d' | '30d';

function fromUtcFor(range: DateRange): string {
  const d = new Date();
  if (range === '24h') d.setHours(d.getHours() - 24);
  else if (range === '7d') d.setDate(d.getDate() - 7);
  else d.setDate(d.getDate() - 30);
  return d.toISOString();
}

export function TestModeActivityViewer(): JSX.Element {
  const api = useApiClient();
  const [range, setRange] = useState<DateRange>('7d');
  const [actor, setActor] = useState<string>('');
  const [rows, setRows] = useState<TestModeRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.retail.sessions.list({
        fromUtc: fromUtcFor(range),
        isTestMode: true,
        ...(actor.trim() ? { actor: actor.trim() } : {}),
        top: 500,
      } as unknown as Record<string, unknown> as never);
      // Defensively shape — sessions list returns either { sessions } or array.
      const raw = (resp as unknown as { sessions?: unknown[]; data?: unknown[] });
      const list = Array.isArray(raw.sessions)
        ? raw.sessions
        : Array.isArray(raw.data)
          ? raw.data
          : [];
      const mapped: TestModeRow[] = list.map((row) => {
        const r = row as Record<string, unknown>;
        const out: TestModeRow = {
          sessionId: String(r['sessionId'] ?? r['id'] ?? ''),
        };
        const startedAt = r['startedAt'];
        const endedAt = r['endedAt'];
        const a = r['actor'] ?? r['officerEmail'];
        const b = r['branch'] ?? r['branchCode'];
        const c = r['cartCount'];
        if (typeof startedAt === 'string') out.startedAt = startedAt;
        if (typeof endedAt === 'string') out.endedAt = endedAt;
        if (typeof a === 'string') out.actor = a;
        if (typeof b === 'string') out.branch = b;
        if (typeof c === 'number') out.cartCount = c;
        return out;
      });
      setRows(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, range, actor]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = (): void => {
    if (rows.length === 0) return;
    const csv = rowsToCsv(rows as unknown as ReadonlyArray<Record<string, unknown>>);
    downloadCsv(`test-mode-activity-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <section className="card">
      <header className="card-header">
        <h3>Test Mode activity</h3>
        <div className="toolbar__spacer" />
        <label className="toolbar__field">
          Range
          <select value={range} onChange={(e) => setRange(e.target.value as DateRange)}>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </label>
        <label className="toolbar__field">
          Actor
          <input
            type="text"
            placeholder="upn or partial"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void load()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </button>
      </header>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="empty">
          No Test Mode activity in this window.
        </div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Session</th>
              <th>Actor</th>
              <th>Branch</th>
              <th>Started</th>
              <th>Ended</th>
              <th>Carts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sessionId}>
                <td>
                  <code>{r.sessionId.slice(0, 8)}…</code>
                </td>
                <td>{r.actor ?? '—'}</td>
                <td>{r.branch ?? '—'}</td>
                <td>{formatLocal(r.startedAt)}</td>
                <td>{formatLocal(r.endedAt)}</td>
                <td>{r.cartCount ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
